import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';

const convertBase64ToBlob = (base64: string, contentType = 'application/pdf') => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });

  return blob;
};

export const downloadBase64 = (b64Data: string, contentType = 'application/pdf') => {
  const blob = convertBase64ToBlob(b64Data, contentType);
  downloadBlob(blob);
};

export const openBase64 = (b64Data: string, contentType = 'application/pdf') => {
  const blob = convertBase64ToBlob(b64Data, contentType);
  openBlob(blob);
};

export const downloadBlob = (blob: Blob, fileName: string = 'file.pdf') => {
  saveAs(blob, fileName);
};

export const openBlob = (blob: Blob) => {
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');
  // Delay cleanup para dar tempo do navegador carregar o PDF na nova aba
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

export const printBlob = (blob: Blob) => {
  const blobUrl = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');

  iframe.style.display = 'none';
  document.body.appendChild(iframe);

  let cleanedUp = false;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    URL.revokeObjectURL(blobUrl);
    if (iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  };

  iframe.onload = function () {
    setTimeout(function () {
      try {
        iframe.focus();
        if (iframe.contentWindow) {
          // Fallback timeout caso afterprint não dispare (ex: usuário fecha aba)
          const fallbackTimeout = setTimeout(cleanup, 60000);

          // Cleanup seguro: espera o diálogo de impressão fechar
          iframe.contentWindow.addEventListener(
            'afterprint',
            () => {
              clearTimeout(fallbackTimeout);
              cleanup();
            },
            { once: true }
          );

          iframe.contentWindow.print();
        }
      } catch (error) {
        console.error('Print error:', error);
        cleanup();
        // Fallback: abrir em nova aba para impressão manual
        const fallbackUrl = URL.createObjectURL(blob);
        window.open(fallbackUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(fallbackUrl), 60000);
      }
    }, 100);
  };

  iframe.onerror = function () {
    console.error('Failed to load PDF in iframe');
    cleanup();
    // Fallback: abrir em nova aba
    const fallbackUrl = URL.createObjectURL(blob);
    window.open(fallbackUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(fallbackUrl), 60000);
  };

  iframe.src = blobUrl;
};

export async function mergePdfs(blobs: Blob[]) {
  const pdfDoc = await PDFDocument.create();

  for (const blob of blobs) {
    const arrayBuffer = await blob.arrayBuffer();
    const pdf = await PDFDocument.load(arrayBuffer);
    const pages = await pdfDoc.copyPages(pdf, pdf.getPageIndices());
    pages.forEach((page) => pdfDoc.addPage(page));
  }

  const pdfBytes = await pdfDoc.save();

  const buffer =
    pdfBytes instanceof Uint8Array ? (pdfBytes.buffer as ArrayBuffer) : (pdfBytes as unknown as ArrayBuffer);
  return new Blob([buffer], { type: 'application/pdf' });
}
